import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listDefinitions,
  getDefinition,
  runReport,
  listRuns,
  getRun,
  downloadRun,
  viewReport,
  createSchedule,
  listSchedules,
  deleteSchedule,
} from './network';
import type { ReportCategory, ReportFormat, ReportRun, ReportSchedule } from './types';

const TERMINAL: readonly string[] = ['COMPLETED', 'FAILED'];

export const reportingKeys = {
  definitions: (params?: { page: number; limit: number; category?: ReportCategory }) =>
    ['reports', 'definitions', params] as const,
  definition: (code: string) => ['reports', 'definitions', 'detail', code] as const,
  runs: (params?: { page: number; limit: number }) => ['reports', 'runs', params] as const,
  run: (id: string) => ['reports', 'runs', 'detail', id] as const,
  schedules: (params?: { page: number; limit: number }) => ['reports', 'schedules', params] as const,
  view: (code: string, params: Record<string, unknown>, pagination: { page: number; limit: number }) =>
    ['reports', 'view', code, params, pagination] as const,
};

export function useReportDefinitions(params: {
  page: number;
  limit: number;
  category?: ReportCategory;
}) {
  return useQuery({
    queryKey: reportingKeys.definitions(params),
    queryFn: () => listDefinitions(params),
  });
}

export function useReportDefinition(code: string | null) {
  return useQuery({
    queryKey: reportingKeys.definition(code ?? ''),
    queryFn: () => getDefinition(code!),
    enabled: !!code,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRunReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      reportCode: string;
      parameters: Record<string, unknown>;
      format: ReportFormat;
    }) => runReport(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reports', 'runs'] });
    },
  });
}

export function useReportRuns(
  params: { page: number; limit: number },
  options: { refetchInterval?: number | false } = {},
) {
  return useQuery({
    queryKey: reportingKeys.runs(params),
    queryFn: () => listRuns(params),
    refetchInterval: options.refetchInterval,
  });
}

export function useReportRun(id: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: reportingKeys.run(id),
    queryFn: () => getRun(id),
    enabled: options.enabled !== false && !!id,
    refetchInterval: (query) => {
      const status = (query.state.data as ReportRun | undefined)?.status;
      if (!status || TERMINAL.includes(status)) return false;
      return 5_000;
    },
  });
}

export function useDownloadRun() {
  return useMutation({
    mutationFn: (id: string) => downloadRun(id),
  });
}

export function useCreateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      reportCode: string;
      parameters: Record<string, unknown>;
      format: ReportFormat;
      cronExpression: string;
      recipients: Array<{ userId: string; email: string }>;
    }) => createSchedule(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reports', 'schedules'] });
    },
  });
}

export function useReportSchedules(params: { page: number; limit: number }) {
  return useQuery({
    queryKey: reportingKeys.schedules(params),
    queryFn: () => listSchedules(params),
  });
}

export function useDeleteSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSchedule(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reports', 'schedules'] });
    },
  });
}

export function useActiveRunPoller(runId: string | null) {
  return useReportRun(runId ?? '', { enabled: !!runId });
}

export function usePollRunsUntilStable(
  baseParams: { page: number; limit: number },
  hasActiveRuns: boolean,
  pollingDeadline: number | null,
) {
  const now = Date.now();
  const pastDeadline = pollingDeadline !== null && now > pollingDeadline;
  const shouldPoll = hasActiveRuns && !pastDeadline;

  return useReportRuns(baseParams, {
    refetchInterval: shouldPoll ? 5_000 : false,
  });
}

export function useReportSchedule(schedule: ReportSchedule) {
  return schedule;
}

export function useViewReport(
  code: string,
  params: Record<string, unknown>,
  pagination: { page: number; limit: number },
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: reportingKeys.view(code, params, pagination),
    queryFn: () => viewReport(code, params, pagination),
    enabled: options.enabled !== false && !!code,
    staleTime: 0, // Freshly query every time we update params
  });
}
